import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { destinations } from '../../data/destinations';
import { calculatePrice, suggestTransports, formatCurrency } from '../../utils/pricing';
import { generateItinerary } from '../../utils/itinerary';
import BookingModal from '../../components/BookingModal/BookingModal';
import BackButton from '../../components/BackButton/BackButton';
import './Destinations.css';

const DestinationDetail = () => {
  const { id } = useParams();
  const destination = destinations.find(d => String(d.id) === String(id));
  const [days, setDays] = useState(destination ? (destination.baseDuration || 7) : 7);
  const [selectedTransport, setSelectedTransport] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const transportOptions = useMemo(() => suggestTransports(days, destination ? destination.location : ''), [days, destination]);
  const transport = useMemo(() => selectedTransport ? transportOptions.find(t => t.id === selectedTransport) : transportOptions[0], [selectedTransport, transportOptions]);

  const estimatedPrice = useMemo(() => calculatePrice(destination.price || 0, Number(days || 1), transport ? transport.multiplier : 1), [destination, days, transport]);

  if (!destination) {
    return (
      <div className="container">
        <p>Destination introuvable.</p>
        <BackButton to="/destinations" label="← Retour aux destinations" />
      </div>
    );
  }

  const booking = {
    destination,
    days: Number(days || 1),
    transport: transport || { id: 'default', name: 'Standard', multiplier: 1 },
    price: estimatedPrice
  };

  return (
    <div className="destinations-page">
      <div className="container">
        {/* Back link moved to bottom of the detail page for better discoverability */}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '2rem', alignItems: 'start' }}>
          <div>
            <img src={destination.image} alt={destination.name} loading="lazy" style={{ width: '100%', borderRadius: '12px', marginBottom: '1rem' }} />
            <h1>{destination.name}</h1>
            <p style={{ color: 'var(--text-light)' }}>{destination.location} · ⭐ {destination.rating}</p>
            <p style={{ marginTop: '1rem' }}>{destination.description}</p>

            {destination.highlights && (
              <section style={{ marginTop: '1.25rem' }}>
                <h3>Points forts</h3>
                <ul>
                  {destination.highlights.map((h, i) => <li key={i}>{h}</li>)}
                </ul>
              </section>
            )}

            {destination.activities && (
              <section style={{ marginTop: '1.25rem' }}>
                <h3>Activités recommandées</h3>
                <div className="activity-list">
                  {destination.activities.map(act => (
                    <div key={act.id} className="activity-item">
                      <div className="activity-title">{act.title}</div>
                      <div className="activity-duration">{act.durationHours}h</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {destination.gallery && destination.gallery.length > 0 && (
              <section style={{ marginTop: '1.25rem' }}>
                <h3>Galerie</h3>
                <div className="detail-gallery">
                  {destination.gallery.map((src, i) => (
                    <a key={i} href={src} target="_blank" rel="noreferrer">
                      <img src={src} alt={`${destination.name} ${i+1}`} />
                    </a>
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside style={{ position: 'relative' }}>
            <div style={{ background: 'white', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-md)' }}>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Prix estimé</strong>
                <div style={{ fontSize: '1.25rem', color: 'var(--primary-color)', fontWeight: 700 }}>{formatCurrency(estimatedPrice)}</div>
              </div>

              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                Durée (jours):
                <input type="number" min="1" value={days} onChange={e => setDays(e.target.value)} style={{ marginLeft: '0.5rem', width: '5rem' }} />
              </label>

              <div style={{ marginBottom: '0.75rem' }}>
                <strong>Transport</strong>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  {transportOptions.map(opt => (
                    <button key={opt.id} onClick={() => setSelectedTransport(opt.id)} type="button" className={`transport-btn ${opt.id === (transport && transport.id) ? 'active' : ''}`}>
                      {opt.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="itinerary">
                <h4>Exemple d'itinéraire</h4>
                {(() => {
                  const list = generateItinerary(destination.activities || [], Number(days || 1), { random: true, uniqueOnly: true });
                  return list.map((act, idx) => (
                    <div key={idx} className="itinerary-day">Jour {idx + 1}: {act ? act.title : 'Temps libre'}</div>
                  ));
                })()}
              </div>

              <button className="btn btn-primary" onClick={() => setShowModal(true)}>Réserver</button>
            </div>
          </aside>
        </div>

        <BackButton to="/destinations" label="← Retour aux destinations" />
      </div>

      <BookingModal open={showModal} onClose={() => setShowModal(false)} booking={booking} />
    </div>
  );
};

export default DestinationDetail;
